import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Save, Trash2, Loader2, Coins, Percent, Euro, Move,
  ToggleLeft, ToggleRight, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../hooks/use-toast';
import {
  adminListWalletPackages,
  adminCreateWalletPackage,
  adminUpdateWalletPackage,
  adminDeleteWalletPackage,
  adminReorderWalletPackages,
} from '../../api/adminWalletPackages';
import { adminWalletPackagesStats } from '../../api/adminWallet';
import TipPresetsEditor from '../../components/admin/TipPresetsEditor';
import AdminCreatorPricesPanel from './AdminCreatorPricesPanel';

/**
 * Admin — Wallet Packages management.
 *
 * Full CRUD over the `wallet_packages` MongoDB collection that backs
 * the Shop page's top-up grid and the `/api/wallet/topup/checkout`
 * endpoint validation. Every change here takes effect on the very next
 * request — no code redeploy needed.
 *
 * Layout:
 *  - Header with back button + page title + total counters.
 *  - Table of packages: name / credits / eur / bonus_pct / active toggle
 *    / sort_order arrows / edit / delete.
 *  - Inline "Add package" row at the bottom.
 *
 * Validations mirror the backend Pydantic model. On save the API
 * response is used as the new source of truth (no optimistic guessing).
 */
const AdminWalletPackages = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pkgs, setPkgs] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // pkg being edited (or 'new')
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, statRows] = await Promise.all([
        adminListWalletPackages(),
        adminWalletPackagesStats().catch(() => []),
      ]);
      setPkgs(Array.isArray(data) ? data : []);
      setStats(Array.isArray(statRows) ? statRows : []);
    } catch (e) {
      toast({
        title: 'Errore caricamento pacchetti',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditing({
      id: null,
      name: '',
      credits: '',
      eur: '',
      bonus_pct: 0,
      active: true,
      sort_order: pkgs.length,
    });
  };

  const startEdit = (p) => setEditing({ ...p });

  const cancelEdit = () => setEditing(null);

  const submitEdit = async () => {
    if (!editing) return;
    const payload = {
      name: (editing.name || '').trim(),
      credits: Number(editing.credits),
      eur: Number(editing.eur),
      bonus_pct: Number(editing.bonus_pct || 0),
      active: !!editing.active,
      sort_order: Number(editing.sort_order || 0),
    };
    if (!payload.name) {
      toast({ title: 'Il nome è obbligatorio', variant: 'destructive' });
      return;
    }
    if (!payload.credits || payload.credits <= 0) {
      toast({ title: 'Crediti > 0 obbligatorio', variant: 'destructive' });
      return;
    }
    if (!payload.eur || payload.eur <= 0) {
      toast({ title: 'Prezzo € > 0 obbligatorio', variant: 'destructive' });
      return;
    }
    if (payload.bonus_pct < 0 || payload.bonus_pct > 100) {
      toast({ title: 'Bonus % deve essere 0-100', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await adminUpdateWalletPackage(editing.id, payload);
        toast({ title: 'Pacchetto aggiornato' });
      } else {
        await adminCreateWalletPackage(payload);
        toast({ title: 'Pacchetto creato' });
      }
      setEditing(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || String(e);
      toast({
        title: 'Errore salvataggio',
        description: typeof detail === 'string' ? detail : 'Errore sconosciuto',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    try {
      await adminUpdateWalletPackage(p.id, { active: !p.active });
      await load();
    } catch (e) {
      toast({
        title: 'Errore toggle',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    }
  };

  const deletePkg = async (p) => {
    if (!window.confirm(`Eliminare il pacchetto "${p.name}" (${p.credits} crediti @ €${p.eur})?`)) return;
    try {
      await adminDeleteWalletPackage(p.id);
      toast({ title: 'Pacchetto eliminato' });
      await load();
    } catch (e) {
      toast({
        title: 'Errore eliminazione',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    }
  };

  const move = async (p, dir) => {
    const idx = pkgs.findIndex((x) => x.id === p.id);
    const swap = pkgs[idx + dir];
    if (!swap) return;
    const items = pkgs.map((x) => ({ id: x.id, sort_order: x.sort_order }));
    const a = items.findIndex((x) => x.id === p.id);
    const b = items.findIndex((x) => x.id === swap.id);
    [items[a].sort_order, items[b].sort_order] = [items[b].sort_order, items[a].sort_order];
    try {
      const fresh = await adminReorderWalletPackages(items);
      setPkgs(fresh);
    } catch (e) {
      toast({
        title: 'Errore riordino',
        description: e?.response?.data?.detail || String(e),
        variant: 'destructive',
      });
    }
  };

  const activeCount = pkgs.filter((p) => p.active).length;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-wallet-packages-page">
      <div className="mx-auto max-w-5xl px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          data-testid="admin-packages-back"
        >
          <ArrowLeft className="h-4 w-4" /> Admin
        </button>

        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold">
              <Coins className="h-6 w-6 gold-text" /> Wallet · Pacchetti Crediti
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Gestisci i pacchetti di ricarica visibili nello Shop. Le modifiche sono attive
              immediatamente — nessun deploy o riavvio necessario.
            </p>
          </div>
          <div className="flex gap-4 text-xs text-muted-foreground">
            <span data-testid="admin-packages-total">
              <b className="text-foreground">{pkgs.length}</b> totali
            </span>
            <span data-testid="admin-packages-active">
              <b className="text-emerald-400">{activeCount}</b> attivi
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin gold-text" />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-background/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2 font-semibold">Ordine</th>
                    <th className="px-3 py-2 font-semibold">Nome</th>
                    <th className="px-3 py-2 text-right font-semibold">Crediti</th>
                    <th className="px-3 py-2 text-right font-semibold">Prezzo €</th>
                    <th className="px-3 py-2 text-right font-semibold">Bonus %</th>
                    <th className="px-3 py-2 text-right font-semibold">c/€</th>
                    <th className="px-3 py-2 text-center font-semibold">Attivo</th>
                    <th className="px-3 py-2 text-right font-semibold">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {pkgs.map((p, i) => {
                    const ratio = p.eur > 0 ? (p.credits / p.eur).toFixed(2) : '—';
                    return (
                      <tr
                        key={p.id}
                        className={`border-t border-border/60 ${!p.active ? 'opacity-50' : ''}`}
                        data-testid={`admin-package-row-${p.id}`}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <button
                              className="rounded p-1 hover:bg-white/5 disabled:opacity-30"
                              disabled={i === 0}
                              onClick={() => move(p, -1)}
                              title="Sposta su"
                              data-testid={`admin-package-move-up-${p.id}`}
                            >
                              <Move className="h-3 w-3 -rotate-90" />
                            </button>
                            <button
                              className="rounded p-1 hover:bg-white/5 disabled:opacity-30"
                              disabled={i === pkgs.length - 1}
                              onClick={() => move(p, 1)}
                              title="Sposta giù"
                              data-testid={`admin-package-move-down-${p.id}`}
                            >
                              <Move className="h-3 w-3 rotate-90" />
                            </button>
                            <span className="ml-1 text-xs tabular-nums text-muted-foreground">
                              {p.sort_order}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 font-medium">{p.name}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {p.credits.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          €{p.eur}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {p.bonus_pct > 0 ? (
                            <span className="rounded bg-emerald-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-300">
                              +{p.bonus_pct}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {ratio}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => toggleActive(p)}
                            className="inline-flex items-center gap-1 text-xs"
                            data-testid={`admin-package-toggle-${p.id}`}
                          >
                            {p.active ? (
                              <ToggleRight className="h-5 w-5 text-emerald-400" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                            )}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => startEdit(p)}
                              className="rounded px-2 py-1 text-xs text-blue-400 hover:bg-blue-500/10"
                              data-testid={`admin-package-edit-${p.id}`}
                            >
                              Modifica
                            </button>
                            <button
                              onClick={() => deletePkg(p)}
                              className="rounded px-2 py-1 text-xs text-red-400 hover:bg-red-500/10"
                              data-testid={`admin-package-delete-${p.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {pkgs.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-8 text-center text-sm text-muted-foreground">
                        Nessun pacchetto. Aggiungi il primo qui sotto.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={startNew}
                className="gold-gradient text-black"
                data-testid="admin-packages-new-btn"
              >
                <Plus className="mr-1.5 h-4 w-4" /> Nuovo pacchetto
              </Button>
            </div>

            {/* Statistics section */}
            <div className="mt-10">
              <div className="mb-3">
                <h2 className="text-lg font-semibold">Statistiche di vendita</h2>                <p className="text-xs text-muted-foreground">
                  Aggregato di tutti i top-up completati (`item_type=wallet_topup`).
                  Include i pacchetti dismessi con vendite storiche.
                </p>
              </div>
              <div className="overflow-hidden rounded-xl border border-border bg-card">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-background/40 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                      <th className="px-3 py-2 font-semibold">Pacchetto</th>
                      <th className="px-3 py-2 text-right font-semibold">Prezzo</th>
                      <th className="px-3 py-2 text-right font-semibold">Acquisti</th>
                      <th className="px-3 py-2 text-right font-semibold">Crediti venduti</th>
                      <th className="px-3 py-2 text-right font-semibold">Fatturato €</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((s) => (
                      <tr
                        key={`${s.id || 'legacy'}-${s.eur}`}
                        className={`border-t border-border/60 ${!s.active ? 'opacity-60' : ''}`}
                        data-testid={`admin-package-stats-${s.eur}`}
                      >
                        <td className="px-3 py-2">
                          <span className="font-medium">{s.name || `€${s.eur}`}</span>
                          {!s.active && (
                            <span className="ml-2 rounded bg-white/5 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                              non attivo
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">€{s.eur}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {s.purchases.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {s.credits_sold.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold">
                          €{s.revenue_eur.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    {stats.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-6 text-center text-sm text-muted-foreground">
                          Nessuna vendita registrata.
                        </td>
                      </tr>
                    )}
                    {stats.length > 0 && (
                      <tr className="border-t-2 border-border bg-background/40 font-semibold">
                        <td className="px-3 py-2">TOTALE</td>
                        <td className="px-3 py-2 text-right">—</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {stats.reduce((a, s) => a + s.purchases, 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {stats.reduce((a, s) => a + s.credits_sold, 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums gold-text">
                          €{stats.reduce((a, s) => a + s.revenue_eur, 0).toFixed(2)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tip Jar presets — moved here Jul 2026 from the Settings tab
                so admin has a single "Wallet · Pacchetti Crediti" page
                covering purchase packages + tip Jar tuning + stats. */}
            <div className="mt-8">
              <TipPresetsEditor />
            </div>

            {/* Phase 52 · Creator price options — quantità di Credits
                che i creator possono scegliere per post/live/o2o. */}
            <div className="mt-8">
              <AdminCreatorPricesPanel />
            </div>
          </>
        )}

        {/* Edit dialog (inline card) */}
        {editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {editing.id ? 'Modifica pacchetto' : 'Nuovo pacchetto'}
                </h2>
                <button
                  onClick={cancelEdit}
                  className="rounded p-1 hover:bg-white/5"
                  data-testid="admin-package-edit-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-3">
                <Field label="Nome">
                  <Input
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="es. Starter, Popular, Pro..."
                    data-testid="admin-package-name-input"
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Crediti">
                    <div className="relative">
                      <Coins className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        min="1"
                        className="pl-7"
                        value={editing.credits}
                        onChange={(e) => setEditing({ ...editing, credits: e.target.value })}
                        placeholder="100"
                        data-testid="admin-package-credits-input"
                      />
                    </div>
                  </Field>
                  <Field label="Prezzo €">
                    <div className="relative">
                      <Euro className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        min="1"
                        className="pl-7"
                        value={editing.eur}
                        onChange={(e) => setEditing({ ...editing, eur: e.target.value })}
                        placeholder="10"
                        data-testid="admin-package-eur-input"
                      />
                    </div>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Bonus % (visuale)">
                    <div className="relative">
                      <Percent className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        className="pl-7"
                        value={editing.bonus_pct}
                        onChange={(e) => setEditing({ ...editing, bonus_pct: e.target.value })}
                        placeholder="0"
                        data-testid="admin-package-bonus-input"
                      />
                    </div>
                  </Field>
                  <Field label="Ordine">
                    <Input
                      type="number"
                      min="0"
                      value={editing.sort_order}
                      onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })}
                      data-testid="admin-package-sort-input"
                    />
                  </Field>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <p className="text-sm font-medium">Attivo</p>
                    <p className="text-xs text-muted-foreground">
                      Se disattivo, il pacchetto NON viene mostrato nello Shop.
                    </p>
                  </div>
                  <Switch
                    checked={!!editing.active}
                    onCheckedChange={(v) => setEditing({ ...editing, active: v })}
                    data-testid="admin-package-active-switch"
                  />
                </div>
                <p className="rounded-lg bg-amber-500/10 p-3 text-[11px] text-amber-200">
                  <b>Nota:</b> il campo <i>Bonus %</i> è puramente informativo (badge UI).
                  I crediti effettivamente accreditati sono SEMPRE il valore del campo <i>Crediti</i>.
                </p>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={cancelEdit}
                  disabled={saving}
                  data-testid="admin-package-cancel-btn"
                >
                  Annulla
                </Button>
                <Button
                  onClick={submitEdit}
                  disabled={saving}
                  className="gold-gradient text-black"
                  data-testid="admin-package-save-btn"
                >
                  {saving ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-1.5 h-4 w-4" />
                  )}
                  Salva
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="mb-1 block text-xs font-semibold text-foreground">{label}</label>
    {children}
  </div>
);

export default AdminWalletPackages;
